import { AsyncButton } from "src/components/common/buttons/AsyncButton.tsx";
import i18n from "i18next";
import { faArrowDownToLine, faLaptopCode, faPlus, faTrash } from "@fortawesome/pro-regular-svg-icons";
import { useFormInput } from "src/components/common/dialogs/formInput/useFormInput.tsx";
import { FormAsyncSelectField } from "src/components/common/forms/fields/FormAsyncSelectField.tsx";
import { FormConfiguratorLuaEditor } from "src/components/views/erp/configurator/scripting/FormConfiguratorLuaEditor.tsx";
import { CatalogPartApi } from "src/api/generated/erp/parts/catalogPart/api/catalogPartApi.ts";
import { CatalogPart } from "src/api/generated/erp/db/types/tables/catalogPart.ts";
import { concatWithPipe, isNullOrBlank } from "src/utils/strings.tsx";
import { requireRule } from "src/components/common/forms/validation.ts";
import { CatalogPartRevisionApi } from "src/api/generated/erp/parts/catalogPart/api/catalogPartRevisionApi.ts";
import { CatalogPartRevisionView } from "src/api/generated/erp/db/types/tables/catalogPartRevisionView.ts";
import { getCatalogPartRevisionStateLabel } from "src/api/generated/erp/db/types/enums/catalogPartRevisionState.ts";
import { FormLazySelectField } from "src/components/common/forms/fields/FormLazySelectField.tsx";
import { useStaticDataGridModel } from "src/components/common/dataGrid/gridModel/useStaticDataGridModel.tsx";
import {
	CatalogPartRevisionBomDataGridRow,
	getCatalogPartRevisionBomDataGridColumns,
	getDefaultRefNo,
} from "src/components/views/erp/parts/catalogPart/CatalogPartRevisionBomDataGrid.utils.tsx";
import { AavoDataGrid } from "src/components/common/dataGrid/AavoDataGrid.tsx";
import { isValidCatalogPartBom } from "src/components/views/erp/parts/catalogPart/partCreationWizard/validation.ts";
import { HorizontalBox } from "../../../../../../common/box/HorizontalBox.tsx";
import { GridBooleanCell } from "@mui/x-data-grid-pro";
import { defineAgentUiContextKey } from "src/components/views/ai/assistant/uiContext/agentUiContextKey.ts";

interface CatalogPartBomStepProps {
	templateCatalogPartId: number | undefined;
	templateCatalogPartRevisionId: number | undefined;
	onSelectTemplateRevision: (templateCatalogPartRevisionId: number) => void;
	bomLines: CatalogPartRevisionBomDataGridRow[];
	onUpdateBom: (bomLines: CatalogPartRevisionBomDataGridRow[]) => void;
	onValidationChange: (isValid: boolean) => void;
	partIsConfigurable: boolean;
}

export const CatalogPartBomStep = ({
	templateCatalogPartId,
	templateCatalogPartRevisionId,
	onSelectTemplateRevision,
	bomLines,
	onUpdateBom,
	onValidationChange,
	partIsConfigurable,
}: CatalogPartBomStepProps) => {
	const showFormDialog = useFormInput();

	const { dataGridProps, getApi, selectedRows } = useStaticDataGridModel<CatalogPartRevisionBomDataGridRow>({
		rows: bomLines,
		gridId: "C00376DEED99C702",
		getRowId: (row) => row.rowId!,
	});

	return (
		<AavoDataGrid<CatalogPartRevisionBomDataGridRow>
			columns={[
				getCatalogPartRevisionBomDataGridColumns({
					bomLineGroupOptions: [],
				}),
				partIsConfigurable && {
					field: "hasConfiguratorScript",
					type: "boolean",
					headerName: i18n.t("configuration_rule"),
					width: 150,
					afterColumn: "isPhantom",
					valueGetter: (_, row) => !isNullOrBlank(row.configuratorTransformationScript),
					renderCell: (params) => (
						<HorizontalBox sx={{ alignItems: "center", gap: 0.5 }}>
							<GridBooleanCell {...params} />
							<AsyncButton
								icon={faLaptopCode}
								size={"small"}
								tooltip={i18n.t("configuration_rule")}
								onClick={async () => {
									await openTransformationScriptEditor(params.row);
								}}
							/>
						</HorizontalBox>
					),
				},
			]}
			processRowUpdate={(updatedRow) => {
				console.log("process");
				upsertRow(updatedRow);
				return updatedRow;
			}}
			editMode={"row"}
			actionBarComponents={
				<>
					<AsyncButton icon={faPlus} tooltip={i18n.t("add_new")} onClick={handleAdd} />
					<AsyncButton icon={faTrash} tooltip={i18n.t("remove")} onClick={handleRemove} />
					<AsyncButton
						label={i18n.t("import_from_template")}
						icon={faArrowDownToLine}
						onClick={handleImportFromTemplate}
						sx={{
							alignSelf: "flex-start",
						}}
					/>
				</>
			}
			agentContext={{
				key: defineAgentUiContextKey("erp.parts.partCreationWizardCatalogPartBom.grid"),
				data: {
					templateCatalogPartId: templateCatalogPartId ?? null,
					templateCatalogPartRevisionId: templateCatalogPartRevisionId ?? null,
				},
				rowFields: ["rowId", "lineCatalogPartId", "referenceNo"],
				selectedRowPathSegment: (row) => `${i18n.t("reference_no")} ${row.referenceNo}`,
			}}
			{...dataGridProps}
		/>
	);

	async function handleRemove() {
		const idsToRemove = new Set(selectedRows.map((item) => item.rowId));
		const updatedBomLines = bomLines.filter((line) => !idsToRemove.has(line.rowId));
		onUpdateBom(updatedBomLines);
		triggerValidation(updatedBomLines);
	}

	function handleAdd() {
		const api = getApi();
		const newRow: Partial<CatalogPartRevisionBomDataGridRow> = {
			rowId: crypto.randomUUID(),
			referenceNo: getDefaultRefNo(api),
			quantity: 1,
			isPhantom: false,
			note: "",
			bomLineGroupId: null,
			configuratorTransformationScript: "",
		};
		upsertRow(newRow as CatalogPartRevisionBomDataGridRow);
	}

	async function openTransformationScriptEditor(row: CatalogPartRevisionBomDataGridRow) {
		const formResult = await showFormDialog<{ script: string }>({
			title: i18n.t("configuration_rule"),
			size: "fullscreen",
			defaultValues: {
				script: row.configuratorTransformationScript,
			},
			content: ({ control }) => (
				<FormConfiguratorLuaEditor
					control={control}
					name={"script"}
					productFamilyVersionId={null} // In wizard, we don't have product family version yet
					catalogPartRevisionId={null} // In wizard, we don't have catalog part revision yet
				/>
			),
		});
		if (formResult == undefined) return;

		upsertRow({
			...row,
			configuratorTransformationScript: formResult.script,
		});
	}

	function upsertRow(row: CatalogPartRevisionBomDataGridRow) {
		const existingIndex = bomLines.findIndex((line) => line.rowId === row.rowId);
		const updatedBomLines =
			existingIndex === -1 ?
				[...bomLines, row]
			:	bomLines.map((line, index) => (index === existingIndex ? row : line));

		onUpdateBom(updatedBomLines);
		triggerValidation(updatedBomLines);
	}

	function triggerValidation(rows: CatalogPartRevisionBomDataGridRow[]) {
		onValidationChange?.(isValidCatalogPartBom(rows));
	}

	async function handleImportFromTemplate() {
		interface FormValues {
			catalogPartId: number;
			catalogPartRevisionId: number;
		}

		const defaultCatalogPartRevisionId =
			templateCatalogPartRevisionId != null ? templateCatalogPartRevisionId
			: templateCatalogPartId != null ?
				await CatalogPartRevisionApi.getLatestCatalogPartRevisionIdForPart({
					catalogPartId: templateCatalogPartId,
				})
			:	undefined;

		const formResult = await showFormDialog<FormValues>({
			title: i18n.t("import_from_template"),
			size: "sm",
			defaultValues: {
				catalogPartId: templateCatalogPartId ?? undefined,
				catalogPartRevisionId: defaultCatalogPartRevisionId ?? undefined,
			},
			submitLabel: i18n.t("import"),
			submitOnEnter: true,
			content: ({ control, watch, setValue }) => {
				const selectedCatalogPartId = watch("catalogPartId");

				return (
					<>
						<FormAsyncSelectField
							control={control}
							name={"catalogPartId"}
							label={i18n.t("part")}
							fetchOptions={async ({ searchQuery, currentSelection }) =>
								CatalogPartApi.getCatalogPartOptions({
									searchQuery,
									currentSelection,
								})
							}
							getOptionKey={(option: CatalogPart) => option.catalogPartId}
							getOptionLabel={(option) =>
								concatWithPipe(option.partNo, option.description_1, option.description_2)
							}
							rules={requireRule()}
							onChange={() => {
								setValue("catalogPartRevisionId", null);
							}}
						/>
						<FormLazySelectField
							control={control}
							name={"catalogPartRevisionId"}
							label={i18n.t("revision")}
							fetchOptions={async () => {
								if (!selectedCatalogPartId) return [];
								return await CatalogPartRevisionApi.getCatalogPartRevisions({
									catalogPartId: selectedCatalogPartId,
								});
							}}
							getOptionKey={(option: CatalogPartRevisionView) => option.catalogPartRevisionId}
							getOptionLabel={(option) =>
								concatWithPipe(
									option.revisionNumber,
									getCatalogPartRevisionStateLabel(option.state),
									option.note,
								)
							}
							rules={requireRule()}
							disabled={!selectedCatalogPartId}
							autoFocus={templateCatalogPartId != null}
						/>
					</>
				);
			},
		});

		if (formResult === undefined) return;

		onSelectTemplateRevision(formResult.catalogPartRevisionId);

		const templateRevisionLines = await CatalogPartRevisionApi.getCatalogPartRevisionLines({
			catalogPartRevisionId: formResult.catalogPartRevisionId,
		});

		onUpdateBom(templateRevisionLines.map((line) => ({ ...line, rowId: crypto.randomUUID() })));
	}
};
